import { pipe } from 'fp-ts/function';
import { map } from 'fp-ts/lib/Either';
import * as t from 'io-ts';

import { Decoding, ErrorAsync, Http } from '@catawiki/runtime-toolkit';

import {
  auctionsRoute,
  categoriesRoute,
  collectionInfoRoute,
  expertRoute,
  lotPricesRoute,
  lotsRoute,
  menuCategoriesRoute,
} from '../constants/apiRoutes';
import { Auction } from '../models/auction';
import { CollectionInfo, CollectionInfoResponse } from '../models/collection';
import { LotPrice } from '../models/lot';
import { setServerHeader } from './request';

export const AuctionsPayload = t.type({ auctions: t.array(Auction) });

export async function fetchAuctions(
  locale: string,
  auctionIds: number[]
): ErrorAsync.PromiseEither<Auction[]> {
  return pipe(
    Http.get(auctionsRoute),
    setServerHeader(),
    Http.setSearch({ locale, ids: auctionIds }, { arrayFormat: 'comma' }),
    Http.send<{ auctions: Auction[] }>()
  )
    .then(Decoding.decodeWith(AuctionsPayload))
    .then(map(({ auctions }) => auctions))
    .catch(ErrorAsync.errorToLeft);
}
export const RemoteLot = t.type({
  auction_id: t.number,
  id: t.number,
  original_image_url: t.string,
  title: t.string,
  url: t.string,
});

export type RemoteLot = t.TypeOf<typeof RemoteLot>;
export const LotsPayload = t.type({ lots: t.array(RemoteLot) });

export async function fetchLots(
  locale: string,
  lotIds: number[]
): ErrorAsync.PromiseEither<RemoteLot[]> {
  return pipe(
    Http.get(lotsRoute),
    setServerHeader(),
    Http.setSearch({ locale, ids: lotIds }, { arrayFormat: 'comma' }),
    Http.send<{ lots: RemoteLot[] }>()
  )
    .then(Decoding.decodeWith(LotsPayload))
    .then(map(({ lots }) => lots))
    .catch(ErrorAsync.errorToLeft);
}

export const LotPricesPayload = t.type({ lots: t.array(LotPrice) });

export async function fetchLotPrices(
  lotIds: number[]
): ErrorAsync.PromiseEither<LotPrice[]> {
  return pipe(
    Http.get(lotPricesRoute),
    setServerHeader(),
    Http.setSearch({ ids: lotIds }, { arrayFormat: 'comma' }),
    Http.send<{ lots: LotPrice[] }>()
  )
    .then(Decoding.decodeWith(LotPricesPayload))
    .then(map(({ lots }) => lots))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteCategory = t.type({
  url: t.string,
  id: t.number,
  name: t.string,
  image_url: t.string,
  color: t.string,
});

export type RemoteCategory = t.TypeOf<typeof RemoteCategory>;

export const RemoteCategoriesPayload = t.type({
  categories: t.array(RemoteCategory),
});
export async function fetchCategories(
  categoryIds: number[],
  locale = 'en'
): ErrorAsync.PromiseEither<RemoteCategory[]> {
  return pipe(
    Http.get(categoriesRoute),
    setServerHeader(),
    Http.setSearch({ locale, ids: categoryIds }, { arrayFormat: 'comma' }),
    Http.send<{ categories: RemoteCategory[] }>()
  )
    .then(Decoding.decodeWith(RemoteCategoriesPayload))
    .then(map(({ categories }) => categories))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteMenuCategoriesPayload = t.type({
  menu: t.array(RemoteCategory),
});
export async function fetchMenuCategories(
  locale = 'en',
  levels = 1
): ErrorAsync.PromiseEither<RemoteCategory[]> {
  return pipe(
    Http.get(menuCategoriesRoute),
    setServerHeader(),
    Http.setSearch({ locale, levels }, { arrayFormat: 'comma' }),
    Http.send<{ categories: RemoteCategory[] }>()
  )
    .then(Decoding.decodeWith(RemoteMenuCategoriesPayload))
    .then(map(({ menu }) => menu))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteExpert = t.type({
  id: t.number,
  name: t.string,
  expertise: t.string,
  gender: t.string,
  main_image: t.string,
  url: t.string,
});

export type RemoteExpert = t.TypeOf<typeof RemoteExpert>;
export const RemoteExpertsPayload = t.type({
  expert: RemoteExpert,
});
export async function fetchExpert(
  expertId: number,
  locale = 'en'
): ErrorAsync.PromiseEither<RemoteExpert> {
  const route = expertRoute(expertId);

  return pipe(
    Http.get(route),
    setServerHeader(),
    Http.setSearch({ locale }, { arrayFormat: 'comma' }),
    Http.send<{ expert: RemoteExpert }>()
  )
    .then(Decoding.decodeWith(RemoteExpertsPayload))
    .then(map(({ expert }) => expert))
    .catch(ErrorAsync.errorToLeft);
}

export async function fetchCollectionInfo(
  locale = 'en',
  collectionId: number
): ErrorAsync.PromiseEither<CollectionInfo> {
  const route = collectionInfoRoute(collectionId);
  return pipe(
    Http.get(route),
    setServerHeader(),
    Http.setSearch({ locale }, { arrayFormat: 'comma' }),
    Http.send()
  )
    .then(Decoding.decodeWith(CollectionInfoResponse))
    .then(map(({ collection }) => collection))
    .catch(ErrorAsync.errorToLeft);
}
